
import { Product, NewsItem, NewsCategory, Subscriber, KeywordLink, Banner, User, Tip, Popup, SiteSettings } from './types';

export const getBasePath = () => {
  if (typeof window === 'undefined') return '';
  
  // Helper to ensure trailing index.php and slashes are cleaned out of base path
  const clean = (val: string) => {
    let cleaned = val.replace(/\/+$/, '');
    cleaned = cleaned.replace(/\/index\.php\/?$/, '');
    return cleaned;
  };

  // 1. FORCED FALLBACK FOR USER'S CUSTOM DOMAIN
  if (window.location.hostname === 'andersonferro.com.br' || window.location.hostname === 'www.andersonferro.com.br') {
    return '/produtos';
  }

  // 2. DYNAMIC BASE INJECTED BY PHP
  if (typeof (window as any).__DYNAMIC_BASE__ !== 'undefined') {
    return clean((window as any).__DYNAMIC_BASE__);
  }

  // 3. SCRIPTS CHECK (Most reliable for subfolder detection in production)
  try {
    const scripts = document.getElementsByTagName('script');
    for (const script of Array.from(scripts)) {
      const src = script.src;
      if (src && src.includes('/assets/')) {
        try {
          const url = new URL(src);
          const match = url.pathname.match(/^(.*)\/assets\//);
          if (match && match[1]) {
             return clean(match[1]);
          }
        } catch (e) {}
      }
    }
  } catch (e) {}

  // 3. Fallback: Check if we are in the admin panel which gives us a clue
  const path = window.location.pathname;
  if (path.includes('/admin')) {
    const segments = path.split('/');
    const adminIndex = segments.indexOf('admin');
    if (adminIndex !== -1) {
      return clean(segments.slice(0, adminIndex).join('/'));
    }
  }

  // Identifica se estamos em ambiente de preview/dev
  const hostname = window.location.hostname;
  const isPreview = hostname.includes('run.app') || hostname.includes('localhost');
  if (isPreview) return '';

  // Se não for preview, e estivermos em uma URL profunda, pode ser um subdiretório
  // Mas evitamos chutar caminhos que parecem rotas
  return '';
};

export const getApiUrl = (settings?: SiteSettings) => {
  let url = '/api/index.php';
  
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) {
    const envUrl = import.meta.env.VITE_API_URL;
    if (envUrl && (envUrl.startsWith('http') || envUrl.startsWith('/api'))) {
      url = envUrl;
    }
  }

  if (typeof window === 'undefined') return url;
  if (url.startsWith('http')) return url;

  // Helper safe path cleaner to prevent index.php from being treated as folder directory
  const cleanBase = (pathStr: string) => {
    let cleaned = pathStr.replace(/\/+$/, '');
    cleaned = cleaned.replace(/\/index\.php\/?$/, ''); // Remove trailing index.php if present
    return cleaned;
  };

  // Use installation URL if available in settings as primary source of truth
  if (settings?.installationUrl) {
    try {
      const instUrl = new URL(settings.installationUrl);
      const cleanPath = cleanBase(instUrl.pathname);
      return `${instUrl.origin}${cleanPath}/api/index.php`;
    } catch (e) {}
  }
  
  const origin = window.location.origin;
  const base = cleanBase(getBasePath()); // "" or "/produtos"
  return `${origin}${base}/api/index.php`;
};

export const getArticleUrl = (slug: string, categoryNameOrSlug?: string) => {
  const base = getBasePath();
  const rootBaseClean = base ? `${base}/` : '/';
  
  let catSlug = 'geral';
  if (categoryNameOrSlug) {
    // If it's already a slug (lowercase, no spaces), use it
    if (categoryNameOrSlug === categoryNameOrSlug.toLowerCase() && !categoryNameOrSlug.includes(' ')) {
      catSlug = categoryNameOrSlug;
    } else {
      // Try to find the category slug from INITIAL_CATEGORIES
      const cat = INITIAL_CATEGORIES.find(c => c.name === categoryNameOrSlug || c.slug === categoryNameOrSlug);
      if (cat) {
        catSlug = cat.slug;
      } else {
        // Fallback: slugify the name
        catSlug = categoryNameOrSlug.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
    }
  }
  
  return `${rootBaseClean}${encodeURIComponent(catSlug)}/${encodeURIComponent(slug)}`;
};

export const API_URL = getApiUrl();

/**
 * Garante que uma URL de imagem seja absoluta ou raiz-relativa.
 * Resolve problemas de imagens quebradas em subpáginas.
 */
export const fixImageUrl = (url: string | undefined, settings?: SiteSettings) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  
  const base = getBasePath(); // "/" or "/produtos"
  const prefix = base === '/' ? '' : base;
  
  // Se tivermos a URL de instalação nas configurações, usamos ela como base
  if (settings?.installationUrl) {
    const baseUrl = settings.installationUrl.replace(/\/$/, '');
    return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  }
  
  // Caso contrário, usamos a origem atual + prefixo do base path
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${prefix}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const formatDatePtBr = (dateInput: string | Date | undefined) => {
    if (!dateInput) return '';
    
    let dateString = '';
    if (dateInput instanceof Date) {
        if (isNaN(dateInput.getTime())) return '';
        dateString = dateInput.toISOString();
    } else {
        dateString = String(dateInput);
    }
    
    // Check if it's already in DD/MM/YYYY format
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = dateString.split('/');
        const months = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
        const monthIndex = parseInt(month, 10) - 1;
        if (monthIndex >= 0 && monthIndex < 12) {
            return `${day}/${months[monthIndex]} ${year}`;
        }
    }
    
    // Safely parse date for Safari
    const safeParseDate = (str: string) => {
        // Se estiver no formato DD/MM/YYYY hh:mm:ss ou DD/MM/YYYY
        if (str.includes('/')) {
            const parts = str.split(' ');
            const dateParts = parts[0].split('/');
            if (dateParts.length === 3) {
                const day = parseInt(dateParts[0], 10);
                const month = parseInt(dateParts[1], 10) - 1;
                const year = parseInt(dateParts[2], 10);
                
                if (parts.length > 1) {
                    const timeParts = parts[1].split(':');
                    if (timeParts.length >= 2) {
                        return new Date(year, month, day, 
                            parseInt(timeParts[0], 10), 
                            parseInt(timeParts[1], 10), 
                            timeParts[2] ? parseInt(timeParts[2], 10) : 0);
                    }
                }
                return new Date(year, month, day);
            }
        }
        
        // Formato ISO ou outros
        const date = new Date(str);
        if (!isNaN(date.getTime())) return date;
        
        // Fallback para formatos "25 Mai 2024" ou semelhantes que podem quebrar no Safari
        // Vamos tentar converter mês abreviado em português (se vier do DB já formatado)
        const monthsPt: Record<string, number> = {
            'jan': 0, 'fev': 1, 'mar': 2, 'abr': 3, 'mai': 4, 'jun': 5,
            'jul': 6, 'ago': 7, 'set': 8, 'out': 9, 'nov': 10, 'dez': 11
        };
        
        const match = str.match(/(\d{1,2})\s+([a-zA-Z]{3})\s+(\d{4})/);
        if (match) {
            const day = parseInt(match[1], 10);
            const monthStr = match[2].toLowerCase();
            const year = parseInt(match[3], 10);
            if (monthsPt[monthStr] !== undefined) {
                return new Date(year, monthsPt[monthStr], day);
            }
        }
        
        return null;
    };
    
    try {
        const date = safeParseDate(dateString);
        if (!date || isNaN(date.getTime())) return dateString;
        
        const daySize = String(date.getDate()).padStart(2, '0');
        const months = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
        const monthLabel = months[date.getMonth()];
        const yearVal = date.getFullYear();
        
        return `${daySize}/${monthLabel} ${yearVal}`;
    } catch (e) {
        return dateString;
    }
};

export const calculateReadingTime = (content: string | undefined, readingTime: string | undefined) => {
    if (readingTime && readingTime !== '5 min' && readingTime !== '5') {
        return readingTime.includes('min') ? readingTime : `${readingTime} min`;
    }
    
    if (!content) return '5 min';
    
    // Remove HTML tags
    const text = content.replace(/<[^>]*>?/gm, '');
    const wordCount = text.split(/\s+/).length;
    // Average reading speed is 200 words per minute
    const time = Math.ceil(wordCount / 200);
    return `${time} min`;
};

export const INITIAL_CATEGORIES: NewsCategory[] = [
  { id: '1', name: 'Tecnologia', slug: 'tecnologia' },
  { id: '2', name: 'Design', slug: 'design' },
  { id: '3', name: 'SEO', slug: 'seo' },
  { id: '4', name: 'Negócios', slug: 'negocios' },
  { id: '5', name: 'Saúde', slug: 'saude' },
  { id: '6', name: 'SEO Local', slug: 'seo-local' },
];

export const INITIAL_USERS: User[] = [
  {
    id: 1,
    username: 'admin',
    name: 'Administrador',
    email: 'admin@exemplo.com',
    phone: '',
    role: 'admin',
    created_at: '2024-01-01 10:00:00'
  }
];

export const INITIAL_POPUPS: Popup[] = [
  {
    id: 'pop-welcome',
    type: 'fixed',
    name: 'Oferta Exclusiva: Mentoria',
    imageUrl: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=800',
    linkUrl: '/?pagina=contato',
    targetCategories: ['all'],
    active: true,
    delay: 5,
    reappearHours: 24
  },
  {
    id: 'pop-seo',
    type: 'fixed',
    name: 'Checklist SEO Grátis',
    imageUrl: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&q=80&w=800',
    linkUrl: '/?pagina=contato',
    targetCategories: ['Marketing Digital', 'Criação de Sites', 'Criação de Blogs'],
    active: true,
    delay: 15,
    reappearHours: 48
  }
];

export const INITIAL_TIPS: Tip[] = [
  {
    id: 'tip-1',
    type: 'relevant',
    title: 'Dica Premium: SEO Local',
    content: 'Aumente suas vendas aparecendo nas buscas da sua região. Nosso Gerador de Cidades cria páginas automáticas para dominar o Google localmente.',
    ctaText: 'Ver Gerador de SEO',
    ctaLink: '/?pagina=produtos',
    keywords: 'SEO, vendas, marketing, cidades, google'
  },
  {
    id: 'tip-2',
    type: 'fixed',
    title: 'Automação para Negócios',
    content: 'Precisa de um sistema exclusivo? Nossa equipe desenvolve soluções personalizadas com IA para escalar sua operação digital.',
    ctaText: 'Solicitar Orçamento',
    ctaLink: '/?pagina=contato',
    keywords: ''
  }
];

export const INITIAL_BANNERS: Banner[] = [
  {
    id: 'b-main',
    title: 'Mentoria de Carreira & Tech 2026',
    imageUrl: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1600',
    link: '/?pagina=contato',
    keywords: '',
    format: 'horizontal',
    active: true,
    clicks: 1250,
    views: 15000
  },
  {
    id: 'b-tech',
    title: 'Baixe Grátis: O Guia Definitivo de SEO Local',
    imageUrl: 'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&q=80&w=1600',
    link: '/?pagina=produtos',
    keywords: '',
    format: 'horizontal',
    categoryId: '1',
    active: true,
    clicks: 450,
    views: 3200
  }
];

export const INITIAL_PRODUCTS: Product[] = [
  {
    id: 'p-1',
    title: 'Gerador de SEO Local v5',
    description: 'A ferramenta definitiva para criar páginas automáticas de cidades e dominar as buscas locais no Google com autoridade regional.',
    imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200',
    link: '/?pagina=produtos',
    category: 'featured',
    views: 1240
  },
  {
    id: 'p-2',
    title: 'Ecossistema Digital Médico',
    description: 'Plataforma completa com site, blog com IA e sistema de captura de leads focada em profissionais da saúde e clínicas.',
    imageUrl: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=1200',
    link: '/?pagina=produtos',
    category: 'featured',
    views: 890
  },
  {
    id: 'p-3',
    title: 'Bot de Vendas WhatsApp IA',
    description: 'Automação inteligente que atende, qualifica e vende para seus clientes 24 horas por dia utilizando a API do Gemini.',
    imageUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200',
    link: '/?pagina=contato',
    category: 'release',
    views: 450
  },
  {
    id: 'p-4',
    title: 'Dashboard Admin Premium',
    description: 'Interface de gestão ultra-rápida para SaaS e E-commerce com métricas em tempo real e design minimalista focado em UX.',
    imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200',
    link: '/?pagina=contato',
    category: 'release',
    views: 320
  },
  {
    id: 'p-5',
    title: 'Finanças 360 AI',
    description: 'SaaS completo de gestão financeira empresarial que utiliza Inteligência Artificial para prever fluxo de caixa e categorizar despesas automaticamente.',
    imageUrl: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&q=80&w=1200',
    link: '/?pagina=produtos',
    category: 'release',
    views: 650
  },
  {
    id: 'p-15',
    title: 'VideoMaker AI Pro',
    description: 'Plataforma de criação de vídeos curtos para redes sociais utilizando avatares realistas e narração profissional por inteligência artificial.',
    imageUrl: 'https://images.unsplash.com/photo-1536240478700-b869070f9279?auto=format&fit=crop&q=80&w=1200',
    link: '/?pagina=contato',
    category: 'release',
    views: 740
  },
  {
    id: 'p-16',
    title: 'FreelaPay Dashboard',
    description: 'Sistema completo de gestão financeira e faturamento para freelancers, com geração de notas fiscais automática e controle de impostos simplificado.',
    imageUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80&w=1200',
    link: '/?pagina=produtos',
    category: 'release',
    views: 530
  },
  {
    id: 'p-17',
    title: 'AutoBlog SEO v2',
    description: 'Ferramenta de automação para blogs que pesquisa tendências, redige artigos otimizados e publica no WordPress com imagens geradas por IA.',
    imageUrl: 'https://images.unsplash.com/photo-1499750310159-52f0f83ad497?auto=format&fit=crop&q=80&w=1200',
    link: '/?pagina=produtos',
    category: 'release',
    views: 920
  }
];

export const INITIAL_NEWS: NewsItem[] = [
  {
    id: 'seo-sp-1',
    slug: 'criacao-de-sites-em-sao-paulo',
    title: 'Criação de Sites em São Paulo',
    excerpt: 'Procurando por Criação de Sites em São Paulo? Somos especialistas em desenvolvimento web na maior metrópole do Brasil. Atendimento local e resultados globais.',
    content: `<h2>Criação de Sites Profissionais em São Paulo</h2>
    <p>Se você busca por <strong>Criação de Sites em São Paulo</strong>, encontrou a parceria ideal. Nossa agência oferece soluções completas para empresas que desejam se destacar no mercado paulistano.</p>
    <p>São Paulo é o centro financeiro do país, e ter uma presença digital forte aqui não é luxo, é sobrevivência. Desenvolvemos sites otimizados para o Google (SEO), garantindo que seu negócio seja encontrado por quem procura seus serviços na região.</p>
    <h3>Por que escolher nossa agência em SP?</h3>
    <ul>
      <li>Conhecimento profundo do mercado local.</li>
      <li>Sites ultra-rápidos que carregam em menos de 2 segundos.</li>
      <li>Design exclusivo focado na experiência do usuário (UX).</li>
    </ul>
    <div class="block-alert block-alert-success">
      <h4>Atendimento Exclusivo SP</h4>
      <p>Oferecemos reuniões presenciais ou online para clientes de São Paulo e Grande SP, garantindo um alinhamento perfeito do projeto.</p>
    </div>
    <details class="block-faq">
      <summary>Vocês atendem todas as zonas de SP?</summary>
      <div class="faq-content">Sim! Atendemos Zona Sul, Norte, Leste, Oeste e Centro, além de toda a região metropolitana.</div>
    </details>`,
    imageUrl: 'https://images.unsplash.com/photo-1543059080-f9b16037f379?auto=format&fit=crop&q=80&w=1200',
    date: '25 Mai 2024',
    category: 'Tecnologia',
    tags: ['SEO Local', 'São Paulo', 'Criação de Sites'],
    readingTime: '4 min',
    views: 320,
    status: 'published'
  }
];

export const INITIAL_SUBSCRIBERS: Subscriber[] = [
  { id: '1', name: 'Roberto Almeida', email: 'roberto@empresa.com.br', phone: '(11) 99999-0000', date: '10/05/2024' }
];

export const INITIAL_KEYWORD_LINKS: KeywordLink[] = [
  { id: 'k-1', keyword: 'Marketing Digital', url: '/?pagina=blog' },
  { id: 'k-2', keyword: 'Fale Conosco', url: '/?pagina=contato' }
];

export const CATEGORIES = [
  { value: 'featured', label: 'Destaque Principal' },
  { value: 'release', label: 'Produtos Recentes (Vertical)' },
  { value: 'deal', label: 'Outros Destaques (Horizontal)' },
] as const;
